import {Box, HBox} from '../../../../components/core/view';
import {Buyer, MostRecentAddress} from '../../../../../src/driver/types/common';
import {CustomText, H5Text} from '../../../../components/core/text';
import React, {useCallback, useEffect, useState} from 'react';
import {
  useColorThemeType,
  useFontTheme,
} from '../../../../appStyles/hooks/theme';

import {Analytics} from '../../../../../src/config/analytics/analytics';
import Call from '../../../../components/core/icon/Call';
import ChatBottomsheet from '@buncha/itemDetails/components/ItemDetail/contactBottomSheet/ChatBottomsheet';
import {ChatModes} from '@buncha/batchDetails/types/Chat';
import ChatOpener from '@buncha/components/composites/ChatOpener';
import {DeliveryLocationStatus} from '../../../../batchDetails/types';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '../../../../../src/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import PersonRoundIcon from '../../../../components/core/icon/PersonRoundIcon';
import {StorageKeys} from '@buncha/config/storage/type';
import {StyleSheet} from 'react-native';
import {Task} from '@buncha/config/onfleet/types';
import {Touchable} from '../../../../components/core/touchable';
import UserNameUpperPill from '@buncha/common/components/VipProfileIcon';
import {UserPhoneNumber} from '@buncha/driver/types/driverOrders';
import {UserTypes} from '@buncha/onBoarding/types';
import font from '@buncha/appStyles/style/font';
import {getClientType} from '@buncha/common/types/common';
import moment from 'moment';
import {storage} from '@buncha/config/storage';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsFocused} from '@react-navigation/native';
import {useIsMeijerStore} from '@buncha/home/hooks/home';

interface BuyerDetailCellProps {
  hideAddress?: boolean;
  buyerDetails: Buyer | undefined;
  orderId?: number;
  buyerAddress?: MostRecentAddress;
  runId?: number;
  conversationId?: string;
  storeId?: number;
  customerTagName?: string;
  bagsUsed?: number;
  currentTask: Task | undefined;
  deliveryLocationStatus?: DeliveryLocationStatus;
}
const BuyerDetailCell: React.FC<BuyerDetailCellProps> = props => {
  const theme = useColorThemeType('main');
  const muli = useFontTheme('muli');
  const [openSheet, setOpenSheet] = useState(false);
  const buyer = props?.buyerDetails;
  const currentTask = props?.currentTask;
  const buyerName = currentTask
    ? currentTask.recipientName
    : `${buyer?.firstName} ${buyer?.lastName ?? ''}`;
  const address = props?.buyerAddress?.address ?? '';
  const isFocus = useIsFocused();
  const storeId = props?.storeId ?? 0;
  const isMeijerStore = useIsMeijerStore(storeId);
  const [usersData, setUsersData] = useState<UserPhoneNumber[]>([]);
  const {body} = useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};
  const [showCallOption, setShowCallOption] = useState(
    props?.deliveryLocationStatus !== DeliveryLocationStatus.delivered,
  );

  const toggleSheet = useCallback(() => {
    setOpenSheet(!openSheet);
    Analytics.eventWithProps('Open contact sheet for driver', {
      type: 'CTA',
      cleint: getClientType(isMeijerStore),
    });
  }, [isMeijerStore, openSheet]);

  const updateUsersData = useCallback((user: Buyer) => {
    const data: UserPhoneNumber = {
      phoneNumber: user.phoneNumber,
      user: user.id,
    };
    setUsersData([data]);
  }, []);

  const handleTimestampUpdate = useCallback(async () => {
    if (!props.orderId) return;
    let deliveryTimestamps: any = {};
    try {
      const deliveryTimestampsString = await storage.getItem(
        StorageKeys.deliveryTimestamps,
      );
      deliveryTimestamps = JSON.parse(deliveryTimestampsString ?? '{}');
    } catch {
      deliveryTimestamps = {};
    }
    const orderDeliveredTimestampString = deliveryTimestamps[props.orderId];
    if (!orderDeliveredTimestampString) return;
    const orderDeliveredTimestamp = moment(orderDeliveredTimestampString);
    setShowCallOption(moment().diff(orderDeliveredTimestamp, 'minutes') < 5);
  }, [props.orderId]);

  useEffect(() => {
    handleTimestampUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.orderId]);

  useEffect(() => {
    if (props?.buyerDetails) updateUsersData(props?.buyerDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <HBox>
      <HBox style={styles.buyerBox}>
        <PersonRoundIcon fill={theme?.icon?.blue?.[300]} />
        <Box style={styles.nameBox}>
          <If condition={props?.customerTagName}>
            <UserNameUpperPill text={props?.customerTagName ?? ''} />
          </If>
          <H5Text
            numberOfLines={1}
            style={[
              styles.textAlignment,
              styles.fullWidth,
              {
                color: theme?.text?.blue?.[300],
                fontFamily: muli?.[700]?.normal,
              },
            ]}>
            {buyerName}
          </H5Text>
          <If condition={props?.bagsUsed}>
            <CustomText
              style={[
                {
                  color: theme?.text.gray[100],
                  ...font.FontSize.fontP12,
                  fontFamily: font.fonts.muliSemiBold,
                },
              ]}>
              {body?.youAreDelivering}
            </CustomText>
          </If>
          <If condition={!props?.hideAddress}>
            <CustomText
              numberOfLines={1}
              style={[
                styles.textAlignment,
                {
                  color: theme?.text?.gray?.[100],
                  fontFamily: muli?.[600]?.normal,
                },
              ]}>
              {address}
            </CustomText>
          </If>
        </Box>
      </HBox>
      <Box style={styles.rightBox}>
        <HBox>
          <If
            condition={props?.hideAddress && props?.orderId && !isMeijerStore}>
            <ChatOpener
              usersData={usersData}
              hideRole={UserTypes.shopper}
              mode={ChatModes.order}
              buyerName={buyerName}
              buyerId={buyer?.id}
              conversationId={props?.conversationId}
              runId={isFocus ? props?.runId : undefined}
              style={styles.chat}
            />
          </If>
          <If condition={showCallOption}>
            <Touchable style={styles.call} onPress={toggleSheet}>
              <Call viewBox="0 -3 25 20" fill={theme?.icon?.green?.[100]} />
            </Touchable>
          </If>
        </HBox>
        <If condition={props?.bagsUsed}>
          <HBox>
            <Box
              style={[
                styles.bagsUsedContainer,
                {backgroundColor: theme?.background.yellow[100]},
              ]}>
              <CustomText
                style={[
                  {
                    color: theme?.text.blue[300],
                    ...font.FontSize.fontP10,
                    fontFamily: font.fonts.muliSemiBold,
                  },
                ]}>
                {body?.boxes?.replace('{bagsUsed}', props?.bagsUsed)}
              </CustomText>
            </Box>
          </HBox>
        </If>
      </Box>
      <If condition={openSheet}>
        <ChatBottomsheet
          isOpen={openSheet}
          onClose={toggleSheet}
          buyerInfo={buyer}
          orderId={props.orderId}
          runId={props?.runId}
          storeId={storeId}
          name={currentTask?.recipientName}
          phoneNumber={currentTask?.recipientPhone}
        />
      </If>
    </HBox>
  );
};

export default React.memo(BuyerDetailCell);

const styles = StyleSheet.create({
  nameBox: {
    alignItems: 'flex-start',
    flex: 1,
    marginLeft: 10,
  },
  textAlignment: {
    textAlign: 'left',
  },
  buyerBox: {
    flex: 0.8,
    marginTop: 10,
    alignItems: 'center',
  },
  bagsUsedContainer: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 10,
    marginTop: 5,
  },
  call: {
    marginLeft: 20,
  },
  rightBox: {
    alignItems: 'flex-end',
    marginTop: 5,
  },
  fullWidth: {
    width: '100%',
  },
  chat: {
    marginBottom: -10,
  },
});
